import {get} from "lodash"
import BaseService from "./Base"

class WebFormService extends BaseService {
    private progressCardDownloads = new Map<number, Promise<void>>()

    async downloadTricociProgressCard(studentProfileId: number): Promise<void> {
        if (!Number.isSafeInteger(studentProfileId) || studentProfileId <= 0) {
            throw new Error("Invalid student profile ID")
        }
        const pending = this.progressCardDownloads.get(studentProfileId)
        if (pending) return pending

        const download = this.fetchAndDownloadTricociProgressCard(studentProfileId)
        this.progressCardDownloads.set(studentProfileId, download)
        try {
            await download
        } finally {
            this.progressCardDownloads.delete(studentProfileId)
        }
    }

    private async fetchAndDownloadTricociProgressCard(studentProfileId: number): Promise<void> {
        const baseURL = process.env.REACT_APP_WEBFORMS_BASE_URL
        if (!baseURL) {
            throw new Error("REACT_APP_WEBFORMS_BASE_URL is not configured")
        }
        // Security review: sending the current session Bearer to web-forms is intentional.
        // TricociProgressCard.md requires it: web-forms validates the token via AuthService
        // before retrieving the student's data, just as generateSapDocuments authenticates below.
        // This build-time URL must identify the deployment's trusted web-forms service;
        // it is not a user-provided download URL. A different origin is supported by design.
        const response = await this.axios.post<Blob>(
            "/tricoci-progress-card/",
            {studentProfileId},
            {baseURL, responseType: "blob", "axios-retry": {retries: 0}}
        )
        const blob = response.data
        // Attachments can arrive with a generic or missing Content-Type. Check the PDF
        // signature instead, so HTML/JSON error pages are not saved as progress cards.
        if (!(await this.hasPdfSignature(blob))) {
            throw new Error("Invalid progress card PDF response")
        }

        const disposition = response.headers["content-disposition"] || ""
        let filename = `tricoci-progress-card-${studentProfileId}.pdf`
        const encodedName = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(disposition)
        const plainName = /filename\s*=\s*(?:"([^"]+)"|([^;]+))/i.exec(disposition)
        const headerName = plainName?.[1] || plainName?.[2]?.trim()
        if (headerName) filename = headerName
        if (encodedName) {
            try {
                filename = decodeURIComponent(encodedName[1].trim())
            } catch {
                // Keep the plain filename or the profile-specific fallback for malformed headers.
            }
        }
        filename = filename.split(/[\\/]/).pop() || `tricoci-progress-card-${studentProfileId}.pdf`

        const url = URL.createObjectURL(blob)
        const link = document.createElement("a")
        try {
            link.href = url
            link.download = filename
            document.body.appendChild(link)
            link.click()
        } finally {
            link.remove()
            // Allow the browser to start consuming the Blob before releasing it.
            setTimeout(() => URL.revokeObjectURL(url), 0)
        }
    }

    private async hasPdfSignature(blob: Blob): Promise<boolean> {
        if (blob.size < 5) return false
        return new Promise<boolean>((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = () => resolve(reader.result === "%PDF-")
            reader.onerror = () => reject(reader.error || new Error("Unable to read progress card PDF"))
            reader.onabort = () => reject(new Error("Progress card PDF read aborted"))
            reader.readAsText(blob.slice(0, 5))
        })
    }

    async generateSapDocuments(payload: {
        slug: string
        sapCheckpointCheckIds: number[]
        departmentId: number
        departmentSubunitId: number
    }): Promise<{success: boolean; batchId: string; total: number}> {
        const baseURL = process.env.REACT_APP_WEBFORMS_BASE_URL
        if (!baseURL) {
            throw new Error("REACT_APP_WEBFORMS_BASE_URL is not configured")
        }
        const response = await this.post("/sap-form/bulk-documents", payload, {baseURL})
        return get(response, "data")
    }

    async getReportForms(params = {}) {
        const response = await this.post("/v1/admin-reports/1098T-form-report/list", params)
        return get(response, "data.data", {})
    }

    async getGenerateForms(params = {}) {
        const response = await this.post("/v1/admin-reports/1098T-form/save", params)
        return get(response, "data.data", {})
    }
}

export default WebFormService
